import { useMemo } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from '@trello/workspace-navigation';

import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useIsNewUser } from './runtimeEligibilityChecks/useIsNewUser';
import { getSporkCffeExperimentalOption } from './getSporkCffeExperimentalOption';
import { useTrelloCrossFlowExperiment } from './useTrelloCrossFlowExperiment';

const experimentFeatureKey = 'trello_xf_spork_branding_positioning_new_users';

export type CrossFlowForSporkNewUsersExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForNewUsersSporkParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}

export const useCrossFlowForNewUsersSpork = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForNewUsersSporkParams): TrelloCrossFlowExperiment<CrossFlowForSporkNewUsersExperimentVariations> => {
  const crossFlow = useCrossFlow();
  const { isEligible: isNewUserEligible } = useIsNewUser();
  const { isEligible: hasEligibleWorkspaces, eligibleWorkspaceOptions } =
    useHasEligibleWorkspaces({
      workspaceId,
    });

  const [{ expanded: _workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [{ hidden: _workspaceNavigationHidden }] =
    useWorkspaceNavigationHidden();
  const workspaceNavigationExpanded =
    !_workspaceNavigationHidden && _workspaceNavigationExpanded;

  const experiment = useTrelloCrossFlowExperiment({
    experimentFeatureKey,
    source,
    eligibilityCheckResults:
      source === 'currentWorkspaceNavigationDrawer'
        ? [
            crossFlow.isEnabled,
            isNewUserEligible,
            hasEligibleWorkspaces,
            workspaceNavigationExpanded,
          ]
        : [crossFlow.isEnabled],
  });

  const [hasValidAaSession] = useHasValidAaSession();

  return useMemo(
    () => ({
      ...experiment,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
          isNewTrelloUser: true,
          ...getSporkCffeExperimentalOption(experiment.cohort),
        },
      ),
    }),
    [
      boardId,
      hasValidAaSession,
      crossFlow,
      eligibleWorkspaceOptions,
      experiment,
      source,
      workspaceId,
    ],
  );
};
